import {
  AppUser,
  getEffectiveOrgSetting,
  IOptionsWithTypes,
  isUserPartOfUsersWithTags,
  spErrorTags,
  spLog,
  spLogTags,
  TOrgTag,
} from "@eventr/common";

import { createSelector } from "reselect";
import { StateCreator } from "zustand";
import { UserAPI } from "../data/User";
import { storeClaims } from "../feat/oauth/oauth.slice";
import { BoundStore, bs } from "./bound.store";
import { followedUserId } from "./followedUser";
import { selectFilterNonItineraryUsers } from "./itineraryState/currentItin.slice";
import { getItineraryMapByID } from "./itineraryState/itineraryMap.slice";
import { nameFormats } from "./NameFormats";

interface UserState {
  experimental: boolean;
  users: AppUser[];
  wpUser: AppUser | undefined;
  isGuest: boolean;
  userClaims?: any;
  tier?: string;
  claimsVersion: number;
}

interface UserActions {
  setExperimental: (id: boolean) => void;
  setWpUser: (user: AppUser) => void;
  setWpUserPartial: (user: Partial<AppUser>) => void;
  getCurrentUser: () => AppUser | undefined;
  isUserPartOfSelection: (selection: IOptionsWithTypes[]) => boolean;
  getUserFromID: (id: number) => AppUser | undefined;
  checkAdmin: () => boolean;
  getUserFromName: (name: string) => AppUser | undefined;
  getUsersWithTags: (deptTags: string[], groupTags: string[]) => AppUser[];
  /** Resolves to whether a claims JWT landed with `/me`. */
  fetchUsers: () => Promise<boolean>;
  getNameFormatted: (user: AppUser, format?: number) => string;
  /**
   * @deprecated use selectItineraryUsers instead
   */
  selectedItineraryUsers: () => AppUser[];
  selectedItineraryGroupsAndDepartments: () => TOrgTag[];
  selectedItineraryUsersOnlyFromGroupsAndDepartments: (userToUse?: AppUser[]) => AppUser[];
}
export interface IUserSlice extends UserState, UserActions {}
export const createUserSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], IUserSlice> = (set, get) => ({
  users: [],
  isGuest: false,
  claimsVersion: 0,
  experimental: false,
  setExperimental: (bool: boolean) => {
    set({ experimental: bool }, false, "setExperimental");
  },

  wpUser: undefined,
  setWpUser: (user: AppUser) => {
    set({ isGuest: !!user?.roles?.includes("guest") }, false, "setWpUser");
    set({ wpUser: user }, false, "setWpUser");
  },
  setWpUserPartial: (user: Partial<AppUser>) => {
    const current = get().wpUser;
    if (!current) {
      return;
    }
    const newUser = { ...current, ...user };
    get().setWpUser(newUser);
  },

  isUserPartOfSelection: (selection: IOptionsWithTypes[]) => {
    const user = get().getCurrentUser();
    if (!selection) return false;
    if (selection.length < 1) return false;
    try {
      return user ? isUserPartOfUsersWithTags(user, selection) : false;
    } catch {
      return false;
    }
  },
  getUserFromID: (id: number) => {
    if (!id) {
      return undefined;
    }
    return get().users.find((user: AppUser) => {
      return user.id === id;
    });
  },
  // The acting user: whoever is signed in, or the user a guest account follows. Viewing
  // another member's itinerary does not change this. See useViewedUserId().
  getCurrentUser: () => {
    const user = get().wpUser;
    if (!user) return undefined;
    const id = followedUserId(user) ?? user.id;
    return id !== undefined ? get().getUserFromID(id) : undefined;
  },
  checkAdmin: () => {
    const user = get().wpUser;
    if (
      user &&
      (user.roles.includes("eventr_manager") || user.roles.includes("administrator") || user.roles.includes("owner"))
    ) {
      return true;
    }
    return false;
  },
  getUserFromName: (name: string) => {
    return get().users.find((user: AppUser) => {
      return user.firstName + " " + user.lastName === name;
    });
  },
  getUsersWithTags: (deptTags: string[], groupTags: string[]) => {
    const foundUsers: AppUser[] = [];

    for (const user of get().users) {
      if (user.id !== 1) {
        if (user.departmentTags && user.departmentTags.length > 0) {
          for (const tag of user.departmentTags) {
            if (deptTags.includes(tag)) {
              if (!foundUsers.includes(user)) {
                foundUsers.push(user);
              }
            }
          }
        }
        if (user.groupTags && user.groupTags.length > 0) {
          for (const tag of user.groupTags) {
            if (groupTags.includes(tag)) {
              if (!foundUsers.includes(user)) {
                foundUsers.push(user);
              }
            }
          }
        }
      }
    }

    return foundUsers;
  },
  // `/me` is the only unconditional writer of the claims in localStorage, and
  // setSelectedOrg deletes them just before calling this. So the two requests are settled
  // independently rather than awaited in sequence: `user/users` is claim-gated server-side
  // and rejects outright on a raw-axios client, and sequencing it first meant one failing
  // directory call left the session with no claims at all until the app was relaunched.
  // Returns whether claims landed, so a caller joining an organisation can retry until
  // they do.
  fetchUsers: async () => {
    const [usersSettled, meSettled] = await Promise.allSettled([UserAPI.getUsers(), UserAPI.getCurrentUser()]);

    let claimsStored = false;

    if (meSettled.status === "fulfilled") {
      const me = meSettled.value;
      if (me.status === "success") {
        if (me.data?.deletedUnix) {
          set({ wpUser: undefined }, false, "imDeleted");
          return false;
        }
        set({ wpUser: me.data }, false, "setWpUser");
        // Absent while the organisation-side user is still being synced into WordPress,
        // which is exactly the window a just-joined member opens the app in.
        claimsStored = Boolean(me.data?.claimsJWT);
        storeClaims(me.data?.claimsJWT);
      }
    } else if (meSettled.reason?.code !== "ERR_CANCELED") {
      spErrorTags("Failed to fetch current user", ["USERS"], meSettled.reason);
    }

    if (usersSettled.status === "fulfilled") {
      set({ users: usersSettled.value.filter((user: AppUser) => userFilterFunc(user)) }, false, "seUsers");
    } else if (usersSettled.reason?.code !== "ERR_CANCELED") {
      spErrorTags("Failed to fetch users", ["USERS"], usersSettled.reason);
    }

    return claimsStored;
  },
  getNameFormatted: (user: AppUser, format?: number) => {
    if (!user) {
      return "";
    }

    const orgConfig = get().orgConfig;
    const rawFirstName = user ? (user.firstName ?? "") : "";
    const rawLastName = user ? (user.lastName ?? "") : "";
    const firstnameInitial = rawFirstName.charAt(0).toUpperCase();
    const lastnameInitial = rawLastName.charAt(0).toUpperCase();
    const capitalizedFirstName = firstnameInitial + rawFirstName.slice(1);
    const capitalizedLastName = lastnameInitial + rawLastName.slice(1);

    if ((user && !user.firstName) || (user && !user.lastName)) {
      return user.email;
    }

    // The itinerary in view decides the format: its primary tag may override the org default.
    // Guests never receive `itinTags`, and an empty tag list resolves straight back to the org value.
    let nameFormat =
      getEffectiveOrgSetting(
        "usernameAppFormat",
        getItineraryMapByID(get().selectedItinID)?.tags,
        orgConfig,
        orgConfig.itinTags ?? [],
      ) || 1;

    if (format) {
      nameFormat = format;
    }

    switch (nameFormat) {
      case nameFormats.firstname_lastname:
        return capitalizedFirstName + " " + capitalizedLastName;
      case nameFormats.firstname_lastname_uppercase:
        return (capitalizedFirstName + " " + capitalizedLastName).toUpperCase();
      case nameFormats.firstname_lastname_lowercase:
        return (capitalizedFirstName + " " + capitalizedLastName).toLowerCase();
      case nameFormats.firstname_lastname_firstname_uppercase:
        return capitalizedFirstName.toUpperCase() + " " + capitalizedLastName;
      case nameFormats.firstname_lastname_lastname_uppercase:
        return capitalizedFirstName + " " + capitalizedLastName.toUpperCase();
      case nameFormats.lastname_firstname:
        return capitalizedLastName + " " + capitalizedFirstName;
      case nameFormats.lastname_firstname_uppercase:
        return (capitalizedLastName + " " + capitalizedFirstName).toUpperCase();
      case nameFormats.lastname_firstname_lowercase:
        return (capitalizedLastName + " " + capitalizedFirstName).toLowerCase();
      case nameFormats.lastname_firstname_firstname_uppercase:
        return capitalizedLastName + " " + capitalizedFirstName.toUpperCase();
      case nameFormats.lastname_firstname_lastname_uppercase:
        return capitalizedLastName.toUpperCase() + " " + capitalizedFirstName;
      case nameFormats.firstnameInitial_lastname:
        return firstnameInitial + " " + capitalizedLastName;
      case nameFormats.firstnameInitial_lastname_uppercase:
        return (firstnameInitial + " " + capitalizedLastName).toUpperCase();
      case nameFormats.firstnameInitial_lastname_lowercase:
        return (firstnameInitial + " " + capitalizedLastName).toLowerCase();
      case nameFormats.firstnameInitial_lastname_firstname_uppercase:
        return firstnameInitial.toUpperCase() + " " + capitalizedLastName;
      case nameFormats.firstnameInitial_lastname_lastname_uppercase:
        return firstnameInitial + " " + capitalizedLastName.toUpperCase();
      case nameFormats.firstname_lastnameInitial:
        return capitalizedFirstName + " " + lastnameInitial;
      case nameFormats.firstname_lastnameInitial_uppercase:
        return (capitalizedFirstName + " " + lastnameInitial).toUpperCase();
      case nameFormats.firstname_lastnameInitial_lowercase:
        return (capitalizedFirstName + " " + lastnameInitial).toLowerCase();
      case nameFormats.firstname_lastnameInitial_firstname_uppercase:
        return capitalizedFirstName.toUpperCase() + " " + lastnameInitial;
      case nameFormats.firstname_lastnameInitial_lastname_uppercase:
        return capitalizedFirstName + " " + lastnameInitial.toUpperCase();
      case nameFormats.lastnameInitial_firstname:
        return lastnameInitial + " " + capitalizedFirstName;
      case nameFormats.lastnameInitial_firstname_uppercase:
        return (lastnameInitial + " " + capitalizedFirstName).toUpperCase();
      case nameFormats.lastnameInitial_firstname_lowercase:
        return (lastnameInitial + " " + capitalizedFirstName).toLowerCase();
      case nameFormats.lastnameInitial_firstname_firstname_uppercase:
        return lastnameInitial + " " + capitalizedFirstName.toUpperCase();
      case nameFormats.lastnameInitial_firstname_lastname_uppercase:
        return lastnameInitial.toUpperCase() + " " + capitalizedFirstName;
      case nameFormats.firstnameInitial_lastnameInitial:
        return firstnameInitial + " " + lastnameInitial;
      case nameFormats.firstnameInitial_lastnameInitial_uppercase:
        return (firstnameInitial + " " + lastnameInitial).toUpperCase();
      case nameFormats.firstnameInitial_lastnameInitial_lowercase:
        return (firstnameInitial + " " + lastnameInitial).toLowerCase();
      case nameFormats.firstnameInitial_lastnameInitial_firstname_lowercase:
        return firstnameInitial.toLowerCase() + " " + lastnameInitial;
      case nameFormats.firstnameInitial_lastnameInitial_lastname_lowercase:
        return firstnameInitial + " " + lastnameInitial.toLowerCase();
      case nameFormats.firstname:
        return capitalizedFirstName;
      case nameFormats.firstname_uppercase:
        return capitalizedFirstName.toUpperCase();
      case nameFormats.firstname_lowercase:
        return capitalizedFirstName.toLowerCase();
      case nameFormats.lastname:
        return capitalizedLastName;
      case nameFormats.lastname_uppercase:
        return capitalizedLastName.toUpperCase();
      case nameFormats.lastname_lowercase:
        return capitalizedLastName.toLowerCase();
      default:
        return capitalizedFirstName + " " + capitalizedLastName;
    }
  },
  /**
   * @deprecated use selectItineraryUsers instead
   */
  selectedItineraryUsers: () => {
    const itineraryUserIds: number[] = [];

    const eventV2Data = get().getEventInfoV2();

    try {
      if (eventV2Data && eventV2Data.data && eventV2Data.data.members) {
        for (const member of eventV2Data.data.members) {
          if (member.type === "User") {
            itineraryUserIds.push(member.option);
          } else if (member.type === "Department" || member.type === "Group") {
            const orgUserFromTag = get().users.filter((orgUser) => {
              if (
                (orgUser.departmentTags ?? []).includes(member.option) ||
                (orgUser.groupTags ?? []).includes(member.option)
              ) {
                return true;
              } else {
                return false;
              }
            });
            if (orgUserFromTag) {
              itineraryUserIds.push(...orgUserFromTag.map((u) => u.id));
            }
          }
        }
        return get().users.filter((user) => {
          return itineraryUserIds.includes(user.id);
        });
      } else {
        /// No members list
        return [];
      }
    } catch (error) {
      spLog("Failed to get itinerary users", error);
      return [];
    }
  },
  selectedItineraryGroupsAndDepartments: () => {
    const orgConfig = get().orgConfig;
    const groupsAndDepartments: TOrgTag[] = [];
    try {
      const eventV2Data = get().getEventInfoV2();
      if (eventV2Data && eventV2Data.data && eventV2Data.data.members) {
        for (const member of eventV2Data.data.members) {
          if (["DepartmentV2", "Department"].includes(member.type)) {
            const dep = orgConfig.departmentTags?.find((tag) => {
              return tag.name === member.option;
            });
            if (dep) groupsAndDepartments.push(dep);
          }
          if (["GroupV2", "Group"].includes(member.type)) {
            const gr = orgConfig.groupTags?.find((tag) => {
              return tag.name === member.option;
            });
            if (gr) groupsAndDepartments.push(gr);
          }
        }
      }
    } catch {
      spLogTags("Error", ["ERROR", "selectedItineraryGroupsAndDepartments"]);
    }

    return groupsAndDepartments;
  },
  selectedItineraryUsersOnlyFromGroupsAndDepartments: (userToUse?: AppUser[]) => {
    const foundUsers: AppUser[] = [];
    const foundUsersIdList: number[] = [];

    const selectedItineraryGroupsAndDepartmentsList = get().selectedItineraryGroupsAndDepartments();
    for (const orgUser of userToUse ?? get().users) {
      if (orgUser.id !== 1) {
        for (const itinTag of selectedItineraryGroupsAndDepartmentsList) {
          if (
            (orgUser.groupTags ?? []).includes(itinTag.name) ||
            (orgUser.departmentTags ?? []).includes(itinTag.name)
          ) {
            if (!foundUsersIdList.includes(orgUser.id)) {
              foundUsers.push(orgUser);
              foundUsersIdList.push(orgUser.id);
            }
          }
        }
      }
    }

    return foundUsers;
  },
});

/// To remove admin accounts and deleted ones
function userFilterFunc(user: AppUser) {
  const roles = Array.isArray(user.roles) ? user.roles : Object.values(user.roles ?? {});

  if (roles.includes("administrator") || user.deletedUnix) return;

  return user;
}

export const getUsersCommaSeparated = (users: IOptionsWithTypes[], onlyItinUsers?: boolean) => {
  const filterNonItinUsers = onlyItinUsers ?? true;
  if (!users || users.length < 1) {
    return "";
  }
  let onlyUsers = users.filter((user) => {
    return user.type === "User";
  });
  if (onlyUsers.length < 1) {
    return "";
  }
  if (filterNonItinUsers) {
    onlyUsers = selectFilterNonItineraryUsers(bs(), onlyUsers);
  }
  const userNames = onlyUsers.map((user) => {
    const appUser = bs().getUserFromID(Number.parseInt(user.option?.toString()));

    if ((appUser?.deletedUnix ?? 0) > 0) {
      return "";
    }
    return appUser ? bs().getNameFormatted(appUser) : "";
  });
  return userNames.filter((s) => s !== "").join(", ");
};

//MARK: SELECTORS
export const selectItineraryUsers = createSelector(
  (state: BoundStore) => state.users,
  (state: BoundStore) => state.getEventInfoV2(),
  (users, eventInfo) => {
    const itineraryUserIds: number[] = [];

    try {
      if (eventInfo && eventInfo.data && eventInfo.data.members) {
        for (const member of eventInfo.data.members) {
          if (member.type === "User") {
            itineraryUserIds.push(member.option);
          } else if (member.type === "Department" || member.type === "Group") {
            const orgUserFromTag = users.filter((orgUser) => {
              if (
                (orgUser.departmentTags ?? []).includes(member.option) ||
                (orgUser.groupTags ?? []).includes(member.option)
              ) {
                return true;
              } else {
                return false;
              }
            });
            if (orgUserFromTag) {
              itineraryUserIds.push(...orgUserFromTag.map((u) => u.id));
            }
          }
        }
        return users.filter((user) => {
          return itineraryUserIds.includes(user.id);
        });
      } else {
        /// No members list
        return [];
      }
    } catch (error) {
      spLog("Failed to get itinerary users", error);
      return [];
    }
  },
);
